// El buscador de la tabla de un lanzamiento —
// ADR-2026-09-08-lanzamientos-buscador.
//
// Se busca contra TODO lo que identifica a un producto, no solo su nombre: el
// SKU, el código de barras, la marca, las etiquetas, los valores de sus
// variantes y el camino de sus categorías. Quien busca "rojo" no está pensando
// "voy a filtrar por el campo valores de la variante", está pensando en el
// producto rojo.
//
// Todo se compara sin acentos ni mayúsculas, y con varias palabras tienen que
// estar TODAS: "remera rojo" es "la remera roja", no "todo lo que sea remera o
// rojo". El que escribe una segunda palabra está achicando la búsqueda.

import { caminoDe, type CategoriaPlana } from "./arbolDeCategorias";

/** Lo mínimo que hace falta para buscar un producto. */
export interface ProductoBuscable {
  id: string;
  nombre: string;
  sku: string | null;
  codigoBarras: string | null;
  marca: string | null;
  tags: string | null;
  variantes: ReadonlyArray<{
    valores: string[];
    sku: string | null;
    codigoBarras: string | null;
  }>;
  /** Ids de sus categorías. */
  categorias: readonly string[];
}

/** Sin acentos, sin mayúsculas y sin espacios de más. */
function normalizar(v: string): string {
  return v
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * Todo lo que un producto "dice", junto y normalizado.
 *
 * De las categorías se usa el CAMINO entero ("Remeras › Oversize") y no solo el
 * nombre de la hoja: buscar "remeras" tiene que encontrar lo que está en
 * "Oversize", porque es donde el usuario cree que está. Es lo que hace que un
 * árbol sirva de filtro.
 *
 * Queda afuera la descripción a propósito: es texto largo y libre, y hace que
 * media tabla coincida con cualquier palabra común. Un resultado que no se
 * entiende por qué apareció es peor que uno que falta.
 */
export function textoDeProducto(
  p: ProductoBuscable,
  caminos: ReadonlyMap<string, string>,
): string {
  const partes: Array<string | null> = [
    p.nombre,
    p.sku,
    p.codigoBarras,
    p.marca,
    p.tags,
    ...p.variantes.flatMap((v) => [...v.valores, v.sku, v.codigoBarras]),
    ...p.categorias.map((id) => caminos.get(id) ?? null),
  ];
  return normalizar(partes.filter((x): x is string => !!x && x.trim() !== "").join(" "));
}

/**
 * Los productos que coinciden. Sin texto devuelve todos.
 *
 * `siempreVisible` es el producto que está abierto en la ficha: se muestra
 * aunque no coincida. Si desapareciera de la lista mientras se lo edita, el
 * formulario de la derecha quedaría sin ninguna fila que lo explique.
 */
export function filtrarProductos<T extends ProductoBuscable>(
  productos: readonly T[],
  consulta: string,
  categorias: readonly CategoriaPlana[],
  siempreVisible?: string | null,
): T[] {
  const palabras = normalizar(consulta).split(/\s+/).filter((t) => t !== "");
  if (palabras.length === 0) return [...productos];

  const caminos = new Map(categorias.map((c) => [c.id, caminoDe(categorias, c.id)]));
  return productos.filter((p) => {
    if (siempreVisible && p.id === siempreVisible) return true;
    const texto = textoDeProducto(p, caminos);
    return palabras.every((t) => texto.includes(t));
  });
}
